/**
 * Replica client-side della derivazione dei target periodici.
 * Mirror di backend/monthly.py:_derive_monthly_target + decomposition lineare.
 * Utilizzato per spiegare nel tooltip "Come ho ricavato questo numero?".
 */

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function fmtNum(v, unita) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
  return unita ? `${s} ${unita}` : s;
}

/**
 * Spiega come è stato ricavato il numero `suggerito` per una cella periodo.
 *
 * @param {Object} args
 * @param {Object} args.kr  - { tipo, baseline, target_committed, target_stretch, unita }
 * @param {Array}  args.quartersAll - lista di celle Q1..Q4 con .etichetta e .suggerito
 * @param {Array}  args.mesiAll - lista di 12 celle mensili con .etichetta ('YYYY-MM') e .suggerito
 * @param {string} args.granularita - 'quarter' | 'mese' | 'settimana'
 * @param {string} args.periodoLabel - es. 'Q3-2026' | '2026-06' | '2026-W24'
 * @param {number} args.suggerito - valore numerico mostrato
 * @returns {string|null}  testo multilinea da mostrare in tooltip; null se non spiegabile
 */
export function formulaForCell({ kr, quartersAll, mesiAll, granularita, periodoLabel, suggerito }) {
  if (!kr || kr.tipo !== 'numerico' || suggerito == null) return null;
  const u = kr.unita;
  const baseline = kr.baseline != null ? Number(kr.baseline) : null;
  const target = kr.target_committed != null ? Number(kr.target_committed) : null;
  if (baseline == null || target == null) return null;

  const delta = target - baseline;
  const direction = delta >= 0 ? '↑' : '↓';

  if (granularita === 'quarter') {
    // Idx 0..3 nel proprio anno
    const idx = (quartersAll || []).findIndex((q) => q.etichetta === periodoLabel);
    const n = idx >= 0 ? idx + 1 : null;
    const head = `Suggerito ${kr.titolo ? '— ' + kr.titolo.slice(0, 40) : ''}`;
    if (n) {
      return [
        `${head}`,
        `Ripartizione lineare nell'anno ${direction}`,
        `baseline ${fmtNum(baseline, u)} → target ${fmtNum(target, u)}`,
        `Q${n}: ${fmtNum(baseline, u)} + (${fmtNum(target, u)} − ${fmtNum(baseline, u)}) × ${n}/4`,
        `= ${fmtNum(suggerito, u)}`,
      ].join('\n');
    }
    return `${head}\nRipartizione lineare nell'anno.\n${fmtNum(suggerito, u)}`;
  }

  if (granularita === 'mese') {
    // Trova il quarter del mese
    const m = parseInt((periodoLabel || '').slice(5, 7), 10);
    if (!m || m < 1 || m > 12) return null;
    const q = Math.ceil(m / 3);
    const monthsInQ = [3 * q - 2, 3 * q - 1, 3 * q];
    const idxInQ = monthsInQ.indexOf(m); // 0,1,2
    const yearPrefix = periodoLabel.slice(0, 4);
    const qLabel = `Q${q}-${yearPrefix}`;
    const targetQ = (quartersAll || []).find((x) => x.etichetta === qLabel)?.suggerito;
    let startVal = baseline;
    let startLabel = `baseline ${fmtNum(baseline, u)}`;
    if (q > 1) {
      const prevQLabel = `Q${q - 1}-${yearPrefix}`;
      const prevQ = (quartersAll || []).find((x) => x.etichetta === prevQLabel)?.suggerito;
      if (prevQ != null) {
        startVal = Number(prevQ);
        startLabel = `${prevQLabel.replace('-' + yearPrefix, '')} = ${fmtNum(prevQ, u)}`;
      }
    }
    if (targetQ == null) {
      return [
        `Suggerito ${MONTH_LABELS[m - 1]} ${yearPrefix}`,
        `Ripartizione lineare nel quarter ${direction}`,
        `Target del Q${q} non disponibile — fallback lineare anno.`,
        `= ${fmtNum(suggerito, u)}`,
      ].join('\n');
    }
    const monthName = MONTH_LABELS[m - 1];
    const n = idxInQ + 1;
    return [
      `Suggerito ${monthName} ${yearPrefix}`,
      `Lineare nel Q${q} ${direction}`,
      `${startLabel} → Q${q} = ${fmtNum(targetQ, u)}`,
      `${monthName}: ${fmtNum(startVal, u)} + (${fmtNum(targetQ, u)} − ${fmtNum(startVal, u)}) × ${n}/3`,
      `= ${fmtNum(suggerito, u)}`,
    ].join('\n');
  }

  if (granularita === 'settimana') {
    // Spiegazione semplificata: lineare dentro il mese (se backend non ha calcolato).
    return [
      `Suggerito settimana ${periodoLabel}`,
      `Ripartizione lineare nel mese ${direction}`,
      `baseline ${fmtNum(baseline, u)} → target ${fmtNum(target, u)}`,
      `= ${fmtNum(suggerito, u)}`,
    ].join('\n');
  }

  return null;
}

/**
 * Spiega la derivazione di un target_periodo (suggerito/impegnato) mostrato
 * nella PaginaPeriodo "Target numerici del periodo".
 *
 * @param {Object} args
 * @param {Object} args.kr           - KR { tipo, baseline, target_committed, unita, titolo }
 * @param {string} args.periodo      - 'quarter' | 'mese' | 'settimana'
 * @param {string} args.etichetta    - es. 'Q3-2026' | '2026-09' | '2026-W36'
 * @param {string} args.ripartizione - 'lineare' | 'crescente' | 'stagionale' (opz.)
 * @param {number} args.suggerito
 * @param {number} args.impegnato    - se diverso da suggerito mostra entrambi
 */
export function formulaForPeriodoTarget({ kr, periodo, etichetta, ripartizione, suggerito, impegnato }) {
  if (!kr || kr.tipo !== 'numerico') return null;
  const u = kr.unita;
  const baseline = kr.baseline != null ? Number(kr.baseline) : null;
  const target = kr.target_committed != null ? Number(kr.target_committed) : null;
  if (baseline == null || target == null || suggerito == null) return null;

  const delta = target - baseline;
  const direction = delta >= 0 ? '↑' : '↓';
  const ripLabel = (ripartizione || 'lineare').toLowerCase();

  const lines = [`Target ${periodo} ${etichetta}`];
  if (kr.titolo) lines.push(kr.titolo.slice(0, 50));
  lines.push(`Ripartizione ${ripLabel} ${direction}`);
  lines.push(`baseline ${fmtNum(baseline, u)} → target ${fmtNum(target, u)}`);

  if (periodo === 'quarter') {
    const q = parseInt((etichetta || 'Q1').replace(/[^\d]/g, '').slice(0, 1) || '1', 10);
    if (ripLabel === 'lineare') {
      lines.push(`Q${q}: ${fmtNum(baseline, u)} + (Δ ${fmtNum(delta, u)}) × ${q}/4`);
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    } else if (ripLabel === 'crescente' || ripLabel === 'a-crescere') {
      lines.push(`Pesi 1·2·3·4 (somma 10); Q${q} pesa ${q}/10`);
      lines.push(`Q${q}: ${fmtNum(baseline, u)} + Δ × somma(1..${q})/10`);
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    } else if (ripLabel === 'stagionale') {
      lines.push(`Pesi stagionali fissi per Q${q}`);
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    } else {
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    }
  } else if (periodo === 'mese') {
    const m = parseInt(etichetta.slice(5, 7), 10);
    if (ripLabel === 'lineare') {
      lines.push(`Mese ${m}: ${fmtNum(baseline, u)} + Δ × ${m}/12`);
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    } else if (ripLabel === 'stagionale') {
      lines.push(`Pesi stagionali fissi su 12 mesi`);
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    } else {
      lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
    }
  } else if (periodo === 'settimana') {
    lines.push(`Ripartizione settimanale ${ripLabel}`);
    lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
  } else {
    lines.push(`= ${fmtNum(suggerito, u)} (suggerito)`);
  }

  if (impegnato != null && Number(impegnato) !== Number(suggerito)) {
    lines.push(`Impegnato dall'utente: ${fmtNum(impegnato, u)} (override)`);
  }
  return lines.join('\n');
}
